/* eslint-env es6 */
import {useState} from 'react';
import {useMutation, useQuery} from '@apollo/client';
import {
  Alert,
  Button,
  DatePicker,
  Form,
  Input,
  Select,
  Spin,
  Switch,
  Table,
  Tooltip,
} from 'antd';
import {
  ArrowDownOutlined,
  CrownOutlined,
  DownloadOutlined,
  FilterOutlined,
  InboxOutlined,
  InfoCircleOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import {useSelector} from 'react-redux';
import {useSearchParams} from 'react-router-dom';
import moment from 'moment';
import {toast} from 'react-toastify';
import DBModal from 'components/modal/modal';
import UserLink from 'components/userLink/UserLink';
import {getVisibleSubTabKeys, makeSelectTabOperations} from 'redux/store/auth';
import {NavTabsEnum} from 'utils/constants';
import {currencyOptions} from 'utils/currencyFormatting';
import {isDabaEmail} from 'utils/helper';
import {enabledPaymentMethod} from 'utils/mock';
import {formatEnumLabel} from '../whatWillYouDo/enumFormat';
import {
  buildCheckoutFilters,
  FLOW_LABELS,
  FLOW_REASONS,
  getReasons,
  getSteps,
  PLATFORMS,
} from './filters';
import {
  CHECKOUT_ABANDONMENTS,
  CHECKOUT_ABANDONMENT_SUMMARY,
  EXPORT_CHECKOUT_ABANDONMENTS,
} from './resolvers';
import styles from './checkoutAbandonment.module.scss';

const valueOrDash = value => value ?? '—';
const enumOptions = values =>
  values.map(value => ({value, label: formatEnumLabel(value)}));
const paymentMethodOptions = enabledPaymentMethod.map(({key, value}) => ({
  value: key,
  label: value,
}));
const platformOptions = PLATFORMS.map(value => ({
  value,
  label: value === 'IOS' ? 'iOS' : formatEnumLabel(value),
}));
const reasonLabel = reason => {
  const label = formatEnumLabel(reason).toLowerCase();
  return label.charAt(0).toUpperCase() + label.slice(1);
};

const checkoutDetails = row => (
  <div className={styles.rowDetails}>
    <h4>Checkout details</h4>
    <dl>
      {[
        ['Step', row.step],
        ['Currency', row.currency],
        ['Amount', row.amount],
        ['Payment method', row.paymentMethod],
        ['Subscription plan', row.subscriptionPlan],
        ['Billing cycle', row.billingCycle],
        ['Platform', row.platform],
        ['App version', row.appVersion],
        ['User ID', row.userId],
        ['Client event ID', row.clientEventId],
        ['Country', row.user?.country],
      ].map(([label, value]) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd>{valueOrDash(value)}</dd>
        </div>
      ))}
    </dl>
  </div>
);

const columns = [
  {
    title: 'Date (UTC)',
    dataIndex: 'createdAt',
    width: 185,
    render: value => moment.utc(value).format('DD MMM YYYY, HH:mm:ss'),
  },
  {
    title: 'User',
    key: 'user',
    width: 230,
    render: (_, row) => {
      const user = row.user;
      if (!user) return row.userId || '—';
      return (
        <div className={styles.user}>
          <UserLink userId={user.id || row.userId}>
            {[user.firstName, user.lastName].filter(Boolean).join(' ') ||
              row.userId}
          </UserLink>
          <span>{user.email}</span>
          <span>{user.country}</span>
        </div>
      );
    },
  },
  {
    title: 'Flow',
    dataIndex: 'flow',
    width: 150,
    render: value => (
      <span className={value === 'DEPOSIT' ? styles.depositTag : styles.proTag}>
        {value === 'DEPOSIT' ? 'Deposit' : 'Daba Pro'}
      </span>
    ),
  },
  {
    title: 'Reason',
    key: 'reason',
    width: 280,
    render: (_, row) => (
      <div className={styles.reasonCell}>
        <span>{reasonLabel(row.reason)}</span>
        {row.otherReasonText && (
          <div className={styles.otherReason}>{row.otherReasonText}</div>
        )}
      </div>
    ),
  },
  {
    title: 'Checkout',
    key: 'checkout',
    width: 170,
    render: (_, row) => (
      <div className={styles.user}>
        <span>
          {row.flow === 'DEPOSIT'
            ? [row.currency, row.amount]
                .filter(value => value != null)
                .join(' ') || '—'
            : row.subscriptionPlan || '—'}
        </span>
        <span>
          {row.flow === 'DEPOSIT' ? row.paymentMethod : row.billingCycle}
        </span>
      </div>
    ),
  },
  {
    title: 'Device',
    key: 'device',
    width: 120,
    render: (_, row) => (
      <div className={styles.user}>
        <span>{row.platform || '—'}</span>
        <span>{row.appVersion}</span>
      </div>
    ),
  },
];

const FlowBreakdown = ({flow, counts, reasons, loading}) => {
  const total = counts.reduce((sum, item) => sum + item.count, 0);
  const reasonCounts = new Map(counts.map(item => [item.reason, item.count]));
  const sorted = [...reasons].sort(
    (a, b) => (reasonCounts.get(b) || 0) - (reasonCounts.get(a) || 0),
  );
  const isDeposit = flow === 'DEPOSIT';
  return (
    <article
      className={`${styles.flowPanel} ${
        isDeposit ? styles.depositPanel : styles.proPanel
      }`}>
      <div className={styles.flowHeader}>
        <span className={styles.flowIcon}>
          {isDeposit ? <ArrowDownOutlined /> : <CrownOutlined />}
        </span>
        <div>
          <h4>{FLOW_LABELS[flow]}</h4>
          <p>
            {loading
              ? 'Loading reports…'
              : `${total.toLocaleString()} abandonments`}
          </p>
        </div>
        <span className={styles.flowTotal}>
          {loading ? '—' : total.toLocaleString()}
        </span>
      </div>
      {!loading && total === 0 && (
        <p className={styles.noReports}>
          No reports for this flow in the current view.
        </p>
      )}
      <dl className={styles.counts}>
        {sorted.map(reason => {
          const count = reasonCounts.get(reason) || 0;
          const percent = total ? Math.round((count / total) * 100) : 0;
          return (
            <div key={reason} className={styles.reasonRow}>
              <dt>{reasonLabel(reason)}</dt>
              <dd>
                <strong>{loading ? '—' : count.toLocaleString()}</strong>
                <span>{loading ? '—' : `${percent}%`}</span>
              </dd>
              <div className={styles.track} aria-hidden="true">
                <span style={{width: `${percent}%`}} />
              </div>
            </div>
          );
        })}
      </dl>
      {!reasons.length && (
        <p className={styles.noReports}>
          The selected reasons do not apply to this flow.
        </p>
      )}
    </article>
  );
};

export const CheckoutAbandonmentPage = () => {
  const [form] = Form.useForm();
  const [exportForm] = Form.useForm();
  const [exportOpen, setExportOpen] = useState(false);
  const useAppliedFilters = Form.useWatch('useAppliedFilters', exportForm);
  const loggedInEmail = useSelector(
    state => state?.auth?.userData?.user?.email ?? '',
  );
  const flow = Form.useWatch('flow', form);
  const [searchParams] = useSearchParams();
  // A user's profile links here with ?userId= to open that user's logs.
  const linkedUserId = searchParams.get('userId')?.trim();
  const [filters, setFilters] = useState(
    linkedUserId ? {userId: linkedUserId} : {},
  );
  const [pagination, setPagination] = useState({page: 1, limit: 50});
  const [validationError, setValidationError] = useState('');
  const operations = useSelector(state =>
    makeSelectTabOperations(state, NavTabsEnum.Analytics),
  );
  const subTabVisible = useSelector(
    state =>
      getVisibleSubTabKeys(
        state?.auth?.userData?.user?.activeAdminTab,
        NavTabsEnum.Analytics,
        ['checkout-abandonment'],
      ).length > 0,
  );
  const canView = Boolean(operations?.view && subTabVisible);

  const list = useQuery(CHECKOUT_ABANDONMENTS, {
    variables: {input: {...filters, pagination}},
    skip: !canView,
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
  });
  const summary = useQuery(CHECKOUT_ABANDONMENT_SUMMARY, {
    variables: {input: filters},
    skip: !canView,
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
  });
  const [exportRows, {loading: exporting}] = useMutation(
    EXPORT_CHECKOUT_ABANDONMENTS,
  );
  const listNode = list.data?.adminCheckoutAbandonments;
  const summaryNode = summary.data?.adminCheckoutAbandonmentSummary;
  const listError =
    list.error?.message ||
    (listNode?.__typename === 'Error' && listNode.message);
  const summaryError =
    summary.error?.message ||
    (summaryNode?.__typename === 'Error' && summaryNode.message);

  const applyFilters = values => {
    try {
      const nextFilters = buildCheckoutFilters(values);
      setFilters(nextFilters);
      setPagination(current => ({...current, page: 1}));
      setValidationError('');
    } catch (error) {
      setValidationError(error.message);
    }
  };

  // The form store outlives the modal, so clear it on every close.
  const closeExport = () => {
    exportForm.resetFields();
    setExportOpen(false);
  };

  const handleExport = async ({useAppliedFilters: useApplied, ...values}) => {
    if (!canView || !operations?.export || exporting) return;
    const {reasons, steps, currencies, platforms} = values;
    const exportFilters = useApplied
      ? filters
      : buildCheckoutFilters({reasons, steps, currencies, platforms});
    const emails = values.emails.map(email => email.trim());
    try {
      const {data} = await exportRows({
        variables: {input: {...exportFilters, emails}},
      });
      const result = data?.exportCheckoutAbandonments;
      if (result?.__typename === 'Error') {
        toast.error(result.message);
      } else if (result?.data?.recipient) {
        toast.success(`Export sent to ${result.data.recipient}`);
        closeExport();
      } else {
        toast.error('Export could not be confirmed. Please try again.');
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  if (!canView) {
    return (
      <Alert
        type="warning"
        showIcon
        message="You do not have permission to view abandoned checkouts."
      />
    );
  }

  const periodLabel =
    filters.dateFrom || filters.dateTo
      ? `${
          filters.dateFrom
            ? moment.utc(filters.dateFrom).format('DD MMM YYYY')
            : 'Beginning'
        } – ${
          filters.dateTo
            ? moment.utc(filters.dateTo).format('DD MMM YYYY')
            : 'Now'
        }`
      : 'All time';
  // A date range counts as one filter.
  const activeFilterCount =
    Object.keys(filters).length -
    Number(Boolean(filters.dateFrom && filters.dateTo));

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <h2>Abandoned checkouts</h2>
          <p>See where checkouts stop. Understand what gets in the way.</p>
        </div>
        {operations?.export && (
          <div className={styles.exportAction}>
            <Tooltip title="Up to 50,000 newest rows, using your applied filters. You choose the daba email it is sent to.">
              <Button
                aria-label="Export CSV"
                icon={<DownloadOutlined />}
                onClick={() => setExportOpen(true)}>
                Export CSV
              </Button>
            </Tooltip>
            <span>Delivered to a daba email</span>
          </div>
        )}
      </header>

      <DBModal
        isOpen={exportOpen}
        destroyOnClose
        handleClose={closeExport}
        content={
          <Form
            form={exportForm}
            name="checkoutExport"
            layout="vertical"
            onFinish={handleExport}
            initialValues={{
              useAppliedFilters: true,
              emails: isDabaEmail(loggedInEmail) ? [loggedInEmail] : [],
            }}>
            <h2 style={{marginTop: 0}}>Export abandoned checkouts</h2>
            <p style={{color: '#595959', marginBottom: 16}}>
              A CSV is sent to every daba email below. If it does not arrive,
              check spam.
            </p>
            <Form.Item
              name="emails"
              label="Emails"
              tooltip="Who receives the CSV. Type an address and press Enter to add more. Only @joindaba.com or @dabafinance.com addresses."
              rules={[
                {required: true, message: 'Add at least one daba email'},
                {
                  validator: async (_, value = []) => {
                    const invalid = value.filter(email => !isDabaEmail(email));
                    if (invalid.length) {
                      throw new Error(
                        `Only daba emails are allowed: ${invalid.join(', ')}`,
                      );
                    }
                  },
                },
              ]}>
              <Select
                mode="tags"
                open={false}
                tokenSeparators={[',', ' ', ';']}
                placeholder="you@joindaba.com"
              />
            </Form.Item>
            <Form.Item
              name="useAppliedFilters"
              label="Use the filters applied on the page"
              valuePropName="checked"
              tooltip="On: the export uses the filters applied on the page. Off: pick reasons, steps, currencies and platforms here instead.">
              <Switch />
            </Form.Item>
            {!useAppliedFilters && (
              <>
                <Form.Item
                  name="reasons"
                  label="Reasons"
                  tooltip="Only checkouts left for one of these reasons. Empty means all reasons.">
                  <Select
                    mode="multiple"
                    placeholder="All reasons"
                    allowClear
                    optionFilterProp="label"
                    options={enumOptions(getReasons())}
                  />
                </Form.Item>
                <Form.Item
                  name="steps"
                  label="Steps"
                  tooltip="Only checkouts left at one of these screens. Empty means all steps.">
                  <Select
                    mode="multiple"
                    placeholder="All steps"
                    allowClear
                    optionFilterProp="label"
                    options={enumOptions(getSteps())}
                  />
                </Form.Item>
                <Form.Item
                  name="currencies"
                  label="Currencies"
                  tooltip="Only checkouts in one of these wallet currencies. Empty means all currencies.">
                  <Select
                    mode="multiple"
                    placeholder="All currencies"
                    allowClear
                    optionFilterProp="label"
                    options={currencyOptions}
                  />
                </Form.Item>
                <Form.Item
                  name="platforms"
                  label="Platforms"
                  tooltip="Only checkouts started on one of these devices. Empty means all platforms.">
                  <Select
                    mode="multiple"
                    placeholder="All platforms"
                    allowClear
                    options={platformOptions}
                  />
                </Form.Item>
              </>
            )}
            <Button
              type="primary"
              htmlType="submit"
              block
              loading={exporting}
              style={{background: '#8d6cfa', borderColor: '#8d6cfa'}}>
              Send export
            </Button>
          </Form>
        }
      />

      <div className={styles.filterPanel}>
        <div className={styles.filterHeading}>
          <span>
            <FilterOutlined /> Filters{' '}
            {activeFilterCount > 0 && <b>{activeFilterCount} applied</b>}
          </span>
          <Tooltip title="Dates use UTC calendar days. End dates include the entire day. Ranges can cover up to 366 days.">
            <span className={styles.timezone}>
              <InfoCircleOutlined /> Dates in UTC
            </span>
          </Tooltip>
        </div>
        <Form
          form={form}
          layout="vertical"
          initialValues={{userId: linkedUserId}}
          onFinish={applyFilters}
          className={styles.filters}>
          <Form.Item
            name="flow"
            label="Flow"
            tooltip="Deposit or Daba Pro checkout. Leave empty for both. Changing it drops reasons and steps that don't apply to the new flow.">
            <Select
              placeholder="All flows"
              allowClear
              options={Object.entries(FLOW_LABELS).map(([value, label]) => ({
                value,
                label,
              }))}
              onChange={nextFlow =>
                form.setFieldsValue({
                  reasons: (form.getFieldValue('reasons') || []).filter(
                    reason => getReasons(nextFlow).includes(reason),
                  ),
                  steps: (form.getFieldValue('steps') || []).filter(step =>
                    getSteps(nextFlow).includes(step),
                  ),
                  ...(nextFlow !== 'DABA_PRO_SUBSCRIPTION' && {
                    subscriptionPlans: [],
                    billingCycles: [],
                  }),
                })
              }
            />
          </Form.Item>
          <Form.Item
            name="reasons"
            label="Reasons"
            tooltip="The reason the user picked when leaving. Matches any selected reason. Only the selected flow's reasons are offered.">
            <Select
              mode="multiple"
              placeholder="All reasons"
              allowClear
              optionFilterProp="label"
              maxTagCount="responsive"
              options={enumOptions(getReasons(flow))}
            />
          </Form.Item>
          <Form.Item
            name="dateFrom"
            label="Start date (UTC)"
            tooltip="First UTC day to include. Leave both dates empty for all time. A range covers at most 366 days.">
            <DatePicker placeholder="Any start date" />
          </Form.Item>
          <Form.Item
            name="dateTo"
            label="End date (UTC, inclusive)"
            tooltip="Last UTC day to include. Logs up to the end of this day are counted.">
            <DatePicker placeholder="Any end date" />
          </Form.Item>
          <Form.Item
            name="steps"
            label="Step"
            tooltip="The checkout screen the user left from. Deposit: currency, method, amount, payment. Daba Pro: plan, payment.">
            <Select
              mode="multiple"
              placeholder="All steps"
              allowClear
              optionFilterProp="label"
              maxTagCount="responsive"
              options={enumOptions(getSteps(flow))}
            />
          </Form.Item>
          <Form.Item
            name="currencies"
            label="Currency"
            tooltip="Wallet currency of the checkout. Logs the app sent without a currency are left out while this is set.">
            <Select
              mode="multiple"
              placeholder="All currencies"
              allowClear
              optionFilterProp="label"
              maxTagCount="responsive"
              options={currencyOptions}
            />
          </Form.Item>
          <Form.Item
            name="paymentMethods"
            label="Payment method"
            tooltip="Deposit method the user chose. Logs without a payment method are left out while this is set.">
            <Select
              mode="multiple"
              placeholder="All payment methods"
              allowClear
              optionFilterProp="label"
              maxTagCount="responsive"
              options={paymentMethodOptions}
            />
          </Form.Item>
          <Form.Item
            name="platforms"
            label="Platform"
            tooltip="Device the checkout was started on.">
            <Select
              mode="multiple"
              placeholder="All platforms"
              allowClear
              options={platformOptions}
            />
          </Form.Item>
          {flow === 'DABA_PRO_SUBSCRIPTION' && (
            <>
              <Form.Item
                name="subscriptionPlans"
                label="Subscription plan"
                tooltip="Daba Pro plan keys, typed exactly as the catalog has them (e.g. PRO_NGX_ONE_YEAR). Press Enter after each one.">
                <Select
                  mode="tags"
                  placeholder="Any plan"
                  allowClear
                  open={false}
                  maxTagCount="responsive"
                />
              </Form.Item>
              <Form.Item
                name="billingCycles"
                label="Billing cycle"
                tooltip="Billing cycle of the Daba Pro checkout, e.g. MONTHLY. Not case-sensitive. Press Enter after each one.">
                <Select
                  mode="tags"
                  placeholder="Any billing cycle"
                  allowClear
                  open={false}
                  maxTagCount="responsive"
                />
              </Form.Item>
            </>
          )}
          <Form.Item
            name="userId"
            label="User ID"
            tooltip="Shows one user's logs only. Opening this page from a user's profile fills it in.">
            <Input placeholder="Any user" allowClear />
          </Form.Item>
          <div className={styles.filterFooter}>
            <span className={styles.scope}>
              <span className={styles.scopeDot} />
              {periodLabel}
              <span className={styles.scopeDivider} />
              {filters.flow ? FLOW_LABELS[filters.flow] : 'Both flows'}
              {filters.reasons?.length
                ? ` · ${filters.reasons.length} reasons`
                : ''}
            </span>
            <div className={styles.actions}>
              <Button
                aria-label="Reset"
                type="text"
                icon={<ReloadOutlined />}
                onClick={() => {
                  form.resetFields();
                  form.setFieldsValue({userId: undefined});
                  applyFilters({});
                }}>
                Reset
              </Button>
              <Button type="primary" htmlType="submit">
                Apply filters
              </Button>
            </div>
          </div>
        </Form>
        {validationError && (
          <Alert type="error" showIcon message={validationError} />
        )}
      </div>

      <section
        aria-label="Breakdown by reason"
        className={styles.summarySection}>
        <div className={styles.sectionHeading}>
          <div>
            <h3>Why users leave</h3>
            <p>Reasons reported at checkout, split by flow.</p>
          </div>
          {!summaryError && (
            <p className={styles.total} aria-live="polite">
              Total abandonments:{' '}
              <strong>
                {summary.loading
                  ? '—'
                  : (summaryNode?.data?.total ?? 0).toLocaleString()}
              </strong>
            </p>
          )}
        </div>
        {summaryError ? (
          <Alert
            type="error"
            showIcon
            message={summaryError}
            action={
              <Button onClick={() => summary.refetch()}>Retry summary</Button>
            }
          />
        ) : (
          <Spin spinning={summary.loading}>
            <div className={styles.breakdown}>
              {Object.keys(FLOW_LABELS)
                .filter(key => !filters.flow || key === filters.flow)
                .map(key => (
                  <FlowBreakdown
                    key={key}
                    flow={key}
                    loading={summary.loading}
                    counts={(summaryNode?.data?.counts || []).filter(
                      item => item.flow === key,
                    )}
                    reasons={FLOW_REASONS[key].filter(
                      reason =>
                        !filters.reasons || filters.reasons.includes(reason),
                    )}
                  />
                ))}
            </div>
          </Spin>
        )}
      </section>

      <section
        aria-label="Checkout abandonment logs"
        className={styles.logsSection}>
        <div className={styles.sectionHeading}>
          <div>
            <h3>
              Checkout logs{' '}
              <span className={styles.logCount}>
                {list.loading
                  ? '—'
                  : (listNode?.pagination?.total || 0).toLocaleString()}
              </span>
            </h3>
            <p>Newest first. Expand a row for the full checkout details.</p>
          </div>
          <span className={styles.timezone}>All timestamps in UTC</span>
        </div>
        {listError ? (
          <Alert
            type="error"
            showIcon
            message={listError}
            action={<Button onClick={() => list.refetch()}>Retry list</Button>}
          />
        ) : (
          <Table
            className={styles.logsTable}
            columns={columns}
            rowKey="id"
            dataSource={listNode?.data || []}
            loading={list.loading}
            scroll={{x: 1180}}
            expandable={{expandedRowRender: checkoutDetails, columnWidth: 40}}
            locale={{
              emptyText: (
                <div className={styles.emptyState}>
                  <span className={styles.emptyIcon}>
                    <InboxOutlined />
                  </span>
                  <h4>No checkouts to show</h4>
                  <p>No checkout abandonments match the applied filters.</p>
                  <span>Try a wider date range or reset your filters.</span>
                </div>
              ),
            }}
            pagination={{
              current: pagination.page,
              pageSize: pagination.limit,
              total: listNode?.pagination?.total || 0,
              showSizeChanger: true,
              pageSizeOptions: ['10', '25', '50', '100'],
              showTotal: total => `${total.toLocaleString()} logs`,
              onChange: (page, limit) =>
                setPagination({
                  page: limit === pagination.limit ? page : 1,
                  limit,
                }),
            }}
          />
        )}
      </section>
    </div>
  );
};
